// TMOS Action Management Page (RAIL System)
// Date: December 9, 2025
// Purpose: Main page for Action Management System with tabbed interface
//
// Features:
// - Tabbed interface for different action views
// - My Actions (assigned to me)
// - Actions I Created
// - Quick action creation button

import React, { useState } from 'react';
import { MyActionsPage } from './MyActionsPage';
import { ActionsICreatedPage } from './ActionsICreatedPage';
import { useLanguage } from '@/state/language/LanguageProvider';
import { CheckCircleIcon, PlusIcon } from '@heroicons/react/24/outline';
import { CreateActionModal } from '@/components/actions/CreateActionModal';

export const ActionManagementPage: React.FC = () => {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState<'my-actions' | 'i-created'>('my-actions');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  return (
    <div className="min-h-screen bg-[var(--color-secondary-bg)]">
      <div className="container mx-auto px-4 py-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2">
              <CheckCircleIcon className="w-5 h-5 text-[var(--color-accent-bg)]" />
              <h1 className="text-xl font-bold text-[var(--color-surface-text)]">
                {t('actions.title') || 'Action Management'}
              </h1>
            </div>
            {/* Quick Action Button */}
            <button
              onClick={() => setIsCreateModalOpen(true)}
              className="flex items-center gap-2 px-4 py-2 bg-[var(--color-accent-bg)] text-[var(--color-accent-text)] rounded-md hover:opacity-90 text-sm"
            >
              <PlusIcon className="w-4 h-4" />
              {t('actions.createAction') || 'Create Action'}
            </button>
          </div>
          <p className="text-xs text-[var(--color-secondary-text)] ml-7">
            {t('actions.subtitle') || 'Manage and track action items across the platform'}
          </p>
        </div>

        {/* Tabs */}
        <div className="mb-6">
          <div className="border-b border-[var(--color-border)]">
            <nav className="-mb-px flex space-x-8 overflow-x-auto">
              <button
                onClick={() => setActiveTab('my-actions')}
                className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                  activeTab === 'my-actions'
                    ? 'border-[var(--color-accent-bg)] text-[var(--color-accent-bg)]'
                    : 'border-transparent text-[var(--color-secondary-text)] hover:text-[var(--color-surface-text)] hover:border-[var(--color-border)]'
                }`}
              >
                {t('actions.assignedToMe') || 'Assigned to me'}
              </button>
              <button
                onClick={() => setActiveTab('i-created')}
                className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                  activeTab === 'i-created'
                    ? 'border-[var(--color-accent-bg)] text-[var(--color-accent-bg)]'
                    : 'border-transparent text-[var(--color-secondary-text)] hover:text-[var(--color-surface-text)] hover:border-[var(--color-border)]'
                }`}
              >
                {t('actions.assignedByMe') || 'Assigned by me'}
              </button>
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <div>
          {activeTab === 'my-actions' && <MyActionsPage key={refreshKey} />}
          {activeTab === 'i-created' && <ActionsICreatedPage key={refreshKey} />}
        </div>
      </div>

      {/* Create Action Modal */}
      <CreateActionModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={() => {
          setRefreshKey(prev => prev + 1); // Refresh action lists
        }}
      />
    </div>
  );
};

